import { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

const XIMILAR_COLLECTION_ID = '009c0941-dffb-4449-9404-cad8f2868160';

/**
 * High-performance shipment scanner using Ximilar Visual Search API
 *
 * Architecture:
 * 1. On mount: Index shipment items into Ximilar collection
 * 2. Loop A (100ms): Barcode scanning - instant 100% match
 * 3. Loop B (500ms): Ximilar visual search - AI-powered similarity
 *
 * @param {Object} options
 * @param {RefObject} options.videoRef - Ref to video element
 * @param {Array} options.shipmentItems - Items in the shipment with images
 * @param {boolean} options.enabled - Whether scanning is active
 * @param {function} options.onMatch - Callback when item is matched
 */
export const useXimilarScanner = ({
  videoRef,
  shipmentItems = [],
  enabled = true,
  autoScan = false, // Set to false for manual capture mode
  onMatch
} = {}) => {
  const [isIndexing, setIsIndexing] = useState(false);
  const [indexedCount, setIndexedCount] = useState(0);
  const [indexError, setIndexError] = useState(null);
  const [scoredItems, setScoredItems] = useState([]);
  const [barcodeMatch, setBarcodeMatch] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [lastSearchResult, setLastSearchResult] = useState(null);

  // Refs
  const barcodeIntervalRef = useRef(null);
  const visualIntervalRef = useRef(null);
  const barcodeReaderRef = useRef(null);
  const lastBarcodeRef = useRef(null);
  const indexedItemsRef = useRef(new Set());
  const shipmentIdRef = useRef(null);

  // Create barcode reader
  const getBarcodeReader = useCallback(() => {
    if (!barcodeReaderRef.current) {
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.QR_CODE,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.ITF,
        BarcodeFormat.DATA_MATRIX
      ]);
      barcodeReaderRef.current = new BrowserMultiFormatReader(hints);
    }
    return barcodeReaderRef.current;
  }, []);

  // Match barcode to shipment items
  const matchBarcodeToItems = useCallback((barcode) => {
    if (!barcode || !shipmentItems.length) return null;

    const barcodeUpper = barcode.toUpperCase();

    for (const shipmentItem of shipmentItems) {
      const item = shipmentItem.item;
      if (!item) continue;

      const itemIdentifiers = [
        item.asin?.toUpperCase(),
        item.fnsku?.toUpperCase(),
        item.merchant_sku?.toUpperCase(),
        ...(item.searchableIdentifiers?.split(',').map(id => id.trim().toUpperCase()) || []),
        ...(item.identifiers?.map(id => id.identifier?.toUpperCase()) || [])
      ].filter(Boolean);

      if (itemIdentifiers.includes(barcodeUpper)) {
        return {
          ...shipmentItem,
          matchType: 'barcode',
          confidence: 1.0,
          matchedBy: barcode
        };
      }
    }

    return null;
  }, [shipmentItems]);

  // Index shipment items into Ximilar collection
  const indexShipmentItems = useCallback(async () => {
    if (!shipmentItems.length) return;

    // Create a unique ID for this shipment based on items
    const newShipmentId = shipmentItems.map(si => si.item?.asin || si.item?.fnsku).sort().join('-');

    // Skip if already indexed this shipment
    if (shipmentIdRef.current === newShipmentId && indexedItemsRef.current.size > 0) {
      console.log('[Ximilar] Shipment already indexed');
      return;
    }

    setIsIndexing(true);
    setIndexError(null);
    setIndexedCount(0);

    try {
      // Prepare records for Ximilar
      const records = [];

      for (const shipmentItem of shipmentItems) {
        const item = shipmentItem.item;
        const imageUrl = item?.images?.[0]?.large_url || item?.image_url;
        const itemId = item?.asin || item?.fnsku || item?.merchant_sku;

        if (!imageUrl || !itemId) continue;

        records.push({
          _url: imageUrl,
          _id: itemId, // Use ASIN/FNSKU as the record ID
          asin: item.asin,
          fnsku: item.fnsku,
          sku: item.merchant_sku,
          title: item.title?.substring(0, 100)
        });
      }

      if (records.length === 0) {
        console.log('[Ximilar] No items with images to index');
        setIsIndexing(false);
        return;
      }

      console.log(`[Ximilar] Indexing ${records.length} items...`);

      const response = await fetch('/api/ximilar/insert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          records,
          collectionId: XIMILAR_COLLECTION_ID
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to index items');
      }

      console.log('[Ximilar] Index result:', result);

      // Track indexed items
      records.forEach(r => indexedItemsRef.current.add(r._id));
      shipmentIdRef.current = newShipmentId;
      setIndexedCount(records.length);

    } catch (err) {
      console.error('[Ximilar] Index error:', err);
      setIndexError(err.message);
    } finally {
      setIsIndexing(false);
    }
  }, [shipmentItems]);

  // Search Ximilar with an image
  const searchXimilar = useCallback(async (imageData) => {
    try {
      const response = await fetch('/api/ximilar/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: imageData, // { _url: "..." } or { _base64: "data:image/..." }
          collectionId: XIMILAR_COLLECTION_ID,
          k: 10
        })
      });

      const result = await response.json();
      setLastSearchResult(result);

      if (!response.ok) {
        throw new Error(result.message || 'Search failed');
      }

      return result;
    } catch (err) {
      console.error('[Ximilar] Search error:', err);
      return { error: err.message };
    }
  }, []);

  // Convert canvas to base64
  const canvasToBase64 = useCallback((canvas) => {
    return canvas.toDataURL('image/jpeg', 0.8);
  }, []);

  // Index items when shipment changes
  useEffect(() => {
    if (shipmentItems.length > 0) {
      indexShipmentItems();
    }
  }, [shipmentItems, indexShipmentItems]);

  // Barcode scanning loop (every 100ms) - only when autoScan is enabled
  useEffect(() => {
    if (!enabled || !autoScan || !videoRef?.current || isIndexing) {
      if (barcodeIntervalRef.current) {
        clearInterval(barcodeIntervalRef.current);
        barcodeIntervalRef.current = null;
      }
      return;
    }

    const scanBarcode = async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.videoWidth === 0) return;

      try {
        const canvas = document.createElement('canvas');
        const size = Math.min(300, video.videoWidth * 0.5, video.videoHeight * 0.5);
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // Crop center
        const sx = (video.videoWidth - size) / 2;
        const sy = (video.videoHeight - size) / 2;
        ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);

        const reader = getBarcodeReader();
        const result = await reader.decodeFromCanvas(canvas);

        if (result) {
          const barcode = result.getText();
          if (barcode === lastBarcodeRef.current) {
            const match = matchBarcodeToItems(barcode);
            if (match) {
              setBarcodeMatch(match);
              onMatch?.(match);
            }
          }
          lastBarcodeRef.current = barcode;
        }
      } catch (err) {
        if (err.name !== 'NotFoundException') {
          console.warn('[Scanner] Barcode error:', err.message);
        }
      }
    };

    barcodeIntervalRef.current = setInterval(scanBarcode, 100);

    return () => {
      if (barcodeIntervalRef.current) {
        clearInterval(barcodeIntervalRef.current);
        barcodeIntervalRef.current = null;
      }
    };
  }, [enabled, videoRef, isIndexing, getBarcodeReader, matchBarcodeToItems, onMatch]);

  // Visual search loop (every 500ms - slower due to API calls) - only when autoScan is enabled
  useEffect(() => {
    if (!enabled || !autoScan || !videoRef?.current || isIndexing || indexedCount === 0) {
      if (visualIntervalRef.current) {
        clearInterval(visualIntervalRef.current);
        visualIntervalRef.current = null;
      }
      return;
    }

    const scanVisual = async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || video.videoWidth === 0) return;

      setIsScanning(true);

      try {
        // Capture frame
        const canvas = document.createElement('canvas');
        canvas.width = 640;
        canvas.height = 480;
        const ctx = canvas.getContext('2d');

        // Draw video frame (center crop)
        const scale = Math.max(640 / video.videoWidth, 480 / video.videoHeight);
        const sw = 640 / scale;
        const sh = 480 / scale;
        const sx = (video.videoWidth - sw) / 2;
        const sy = (video.videoHeight - sh) / 2;
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, 640, 480);

        const base64 = canvasToBase64(canvas);

        // Search Ximilar
        const result = await searchXimilar({ _base64: base64 });

        // Ximilar response format: answer_records (matches) and answer_distances (similarity scores)
        if (result.answer_records && result.answer_records.length > 0) {
          const answerRecords = result.answer_records;
          const answerDistances = result.answer_distances || [];

          // Map Ximilar results to shipment items
          const scores = answerRecords.map((record, index) => {
            const distance = answerDistances[index] || 0;
            // Distance is smaller = more similar, convert to similarity score (1 - distance)
            const similarity = Math.max(0, Math.min(1, 1 - distance));

            const matchedItem = shipmentItems.find(si => {
              const item = si.item;
              return item?.asin === record._id ||
                     item?.fnsku === record._id ||
                     item?.merchant_sku === record._id;
            });

            return {
              ...matchedItem,
              itemId: record._id,
              similarity: similarity,
              confidence: similarity,
              matchType: 'ximilar',
              ximilarData: record,
              distance: distance
            };
          }).filter(s => s.item); // Only include items we have

          setScoredItems(scores);

          // Check for high-confidence match
          const topMatch = scores[0];
          if (topMatch && topMatch.confidence > 0.75) {
            onMatch?.({
              ...topMatch,
              matchType: 'ximilar',
              confidence: topMatch.confidence
            });
          }
        }

      } catch (err) {
        console.error('[Ximilar] Visual scan error:', err);
      } finally {
        setIsScanning(false);
      }
    };

    visualIntervalRef.current = setInterval(scanVisual, 500);

    return () => {
      if (visualIntervalRef.current) {
        clearInterval(visualIntervalRef.current);
        visualIntervalRef.current = null;
      }
    };
  }, [enabled, videoRef, isIndexing, indexedCount, shipmentItems, canvasToBase64, searchXimilar, onMatch]);

  // Scan a static image
  const scanImage = useCallback(async (imageSource) => {
    if (indexedCount === 0) {
      return { type: 'error', error: 'No items indexed yet' };
    }

    setIsScanning(true);

    try {
      let imageData;

      if (imageSource instanceof File) {
        // Convert file to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageSource);
        });
        imageData = { _base64: base64 };
      } else if (typeof imageSource === 'string') {
        if (imageSource.startsWith('data:')) {
          imageData = { _base64: imageSource };
        } else {
          imageData = { _url: imageSource };
        }
      } else {
        throw new Error('Invalid image source');
      }

      // First try barcode
      if (imageSource instanceof File) {
        const img = await new Promise((resolve, reject) => {
          const imgEl = new Image();
          imgEl.onload = () => resolve(imgEl);
          imgEl.onerror = reject;
          imgEl.src = URL.createObjectURL(imageSource);
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        try {
          const reader = getBarcodeReader();
          const barcodeResult = await reader.decodeFromCanvas(canvas);
          if (barcodeResult) {
            const barcode = barcodeResult.getText();
            const match = matchBarcodeToItems(barcode);
            if (match) {
              setBarcodeMatch(match);
              onMatch?.(match);
              return { type: 'barcode', match, barcode };
            }
          }
        } catch (e) {
          // No barcode found
        }
      }

      // Search Ximilar
      const result = await searchXimilar(imageData);

      if (result.error) {
        return { type: 'error', error: result.error };
      }

      // Ximilar response format: answer_records (matches) and answer_distances (similarity scores)
      if (result.answer_records && result.answer_records.length > 0) {
        const answerRecords = result.answer_records;
        const answerDistances = result.answer_distances || [];

        const scores = answerRecords.map((record, index) => {
          const distance = answerDistances[index] || 0;
          // Distance is smaller = more similar, convert to similarity score (1 - distance)
          const similarity = Math.max(0, Math.min(1, 1 - distance));

          const matchedItem = shipmentItems.find(si => {
            const item = si.item;
            return item?.asin === record._id ||
                   item?.fnsku === record._id ||
                   item?.merchant_sku === record._id;
          });

          return {
            ...matchedItem,
            itemId: record._id,
            similarity: similarity,
            confidence: similarity,
            matchType: 'ximilar',
            ximilarData: record,
            distance: distance
          };
        }).filter(s => s.item);

        setScoredItems(scores);

        const topMatch = scores[0];
        if (topMatch && topMatch.confidence > 0.5) {
          onMatch?.(topMatch);
          return {
            type: 'ximilar',
            match: topMatch,
            scores,
            rawResponse: result,
            processingTime: result.processingTime
          };
        }

        return { type: 'none', scores, rawResponse: result };
      }

      return { type: 'none', rawResponse: result };

    } catch (err) {
      console.error('[Ximilar] Image scan error:', err);
      return { type: 'error', error: err.message };
    } finally {
      setIsScanning(false);
    }
  }, [indexedCount, shipmentItems, getBarcodeReader, matchBarcodeToItems, searchXimilar, onMatch]);

  // Reset
  const reset = useCallback(() => {
    setBarcodeMatch(null);
    setScoredItems([]);
    setLastSearchResult(null);
    lastBarcodeRef.current = null;
  }, []);

  // Manual capture from video - captures current frame and searches
  const captureAndSearch = useCallback(async () => {
    const video = videoRef?.current;
    if (!video || video.readyState < 2 || video.videoWidth === 0) {
      return { type: 'error', error: 'Camera not ready' };
    }

    if (indexedCount === 0) {
      return { type: 'error', error: 'No items indexed yet' };
    }

    setIsScanning(true);

    try {
      // Capture frame from video
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      const base64 = canvasToBase64(canvas);

      // First try barcode detection
      try {
        const reader = getBarcodeReader();
        const barcodeResult = await reader.decodeFromCanvas(canvas);
        if (barcodeResult) {
          const barcode = barcodeResult.getText();
          const match = matchBarcodeToItems(barcode);
          if (match) {
            setBarcodeMatch(match);
            onMatch?.(match);
            return { type: 'barcode', match, barcode };
          }
        }
      } catch (e) {
        // No barcode found, continue to visual search
      }

      // Search Ximilar
      const result = await searchXimilar({ _base64: base64 });

      if (result.error) {
        return { type: 'error', error: result.error, rawResponse: result };
      }

      // Ximilar response format: answer_records (matches) and answer_distances (similarity scores)
      if (result.answer_records && result.answer_records.length > 0) {
        const answerRecords = result.answer_records;
        const answerDistances = result.answer_distances || [];

        const scores = answerRecords.map((record, index) => {
          const distance = answerDistances[index] || 0;
          const similarity = Math.max(0, Math.min(1, 1 - distance));

          const matchedItem = shipmentItems.find(si => {
            const item = si.item;
            return item?.asin === record._id ||
                   item?.fnsku === record._id ||
                   item?.merchant_sku === record._id;
          });

          return {
            ...matchedItem,
            itemId: record._id,
            similarity: similarity,
            confidence: similarity,
            matchType: 'ximilar',
            ximilarData: record,
            distance: distance
          };
        }).filter(s => s.item);

        setScoredItems(scores);

        const topMatch = scores[0];
        if (topMatch && topMatch.confidence > 0.5) {
          onMatch?.(topMatch);
          return {
            type: 'ximilar',
            match: topMatch,
            scores,
            rawResponse: result,
            processingTime: result.processingTime
          };
        }

        return { type: 'none', scores, rawResponse: result };
      }

      return { type: 'none', rawResponse: result };

    } catch (err) {
      console.error('[Ximilar] Capture error:', err);
      return { type: 'error', error: err.message };
    } finally {
      setIsScanning(false);
    }
  }, [videoRef, indexedCount, shipmentItems, canvasToBase64, getBarcodeReader, matchBarcodeToItems, searchXimilar, onMatch]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (barcodeIntervalRef.current) clearInterval(barcodeIntervalRef.current);
      if (visualIntervalRef.current) clearInterval(visualIntervalRef.current);
    };
  }, []);

  return {
    // State
    isIndexing,
    indexedCount,
    indexError,
    isScanning,

    // Results
    scoredItems,
    barcodeMatch,
    lastSearchResult,

    // Actions
    reset,
    scanImage,
    captureAndSearch, // Manual capture from video
    reindex: indexShipmentItems
  };
};

export default useXimilarScanner;
